import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const NewsletterSection = () => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e?.preventDefault();
    
    if (!email) return;
    
    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubscribed(true);
      setIsLoading(false);
      setEmail('');
    }, 1500);
  };

  const benefits = [
    {
      icon: 'Percent',
      title: 'Exclusive Deals',
      description: 'Get up to 30% off on selected tours and experiences'
    },
    {
      icon: 'MapPin',
      title: 'New Destinations',
      description: 'Be the first to know about our latest destination additions'
    },
    {
      icon: 'Calendar',
      title: 'Travel Tips',
      description: 'Weekly travel guides and insider tips from local experts'
    },
    {
      icon: 'Gift',
      title: 'Special Offers',
      description: 'Birthday discounts and loyalty rewards just for subscribers'
    }
  ];

  if (isSubscribed) {
    return (
      <section className="py-16 lg:py-24 bg-gradient-to-br from-primary via-primary/90 to-accent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 lg:p-12">
            <div className="w-20 h-20 bg-success rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="Check" size={40} color="white" />
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Welcome to the TourEase Family!
            </h2>
            
            <p className="text-xl text-white/90 mb-8">
              Thank you for subscribing! Check your inbox for a special welcome offer.
            </p>
            
            <div className="flex items-center justify-center space-x-6 text-white/80">
              <div className="flex items-center space-x-2">
                <Icon name="Mail" size={20} />
                <span>Weekly newsletters</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Percent" size={20} />
                <span>Exclusive deals</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Gift" size={20} />
                <span>Special offers</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-primary via-primary/90 to-accent">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="text-white">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Never Miss an Adventure
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Subscribe to our newsletter and get exclusive travel deals, destination guides, and insider tips delivered straight to your inbox.
            </p>

            {/* Benefits Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
              {benefits?.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={benefit?.icon} size={20} color="white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">{benefit?.title}</h3>
                    <p className="text-sm text-white/80">{benefit?.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Social Proof */}
            <div className="flex items-center space-x-4 text-white/80">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4]?.map((i) => (
                  <div key={i} className="w-8 h-8 bg-white/20 rounded-full border-2 border-white/30"></div>
                ))}
              </div>
              <span className="text-sm">Join 25,000+ travelers already subscribed</span>
            </div>
          </div>

          {/* Newsletter Form */}
          <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-12">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Mail" size={32} className="text-primary" />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-2">
                Get Travel Inspiration
              </h3>
              <p className="text-muted-foreground">
                Subscribe now and receive a 15% discount on your first booking!
              </p>
            </div>

            <form onSubmit={handleSubscribe} className="space-y-6">
              <div>
                <Input
                  type="email"
                  label="Email Address"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  required
                  className="w-full"
                />
              </div>

              <Button
                type="submit"
                size="lg"
                loading={isLoading}
                iconName="Send"
                iconPosition="right"
                className="w-full"
                disabled={!email || isLoading}
              >
                {isLoading ? 'Subscribing...' : 'Subscribe Now'}
              </Button>
            </form>

            {/* Trust Indicators */}
            <div className="mt-6 pt-6 border-t border-border">
              <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <Icon name="Shield" size={16} />
                  <span>Spam-free</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Clock" size={16} />
                  <span>Weekly updates</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="X" size={16} />
                  <span>Unsubscribe anytime</span>
                </div>
              </div>
            </div>

            {/* Discount Badge */}
            <div className="mt-6 bg-gradient-to-r from-accent to-secondary text-white rounded-2xl p-4 text-center">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Icon name="Gift" size={20} />
                <span className="font-bold">Welcome Bonus</span>
              </div>
              <p className="text-sm">Get 15% off your first tour booking when you subscribe!</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSection;